import { useState, useEffect, useRef } from "react";
import { MapPin, Clock } from "../common/Icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TournamentsSection = () => {
  const [tournaments, setTournaments] = useState([]);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  const STATIC = [
    {
      id: 1,
      name: "Campeonato de Invierno 2025",
      date: "2025-12-15",
      location: "Arena del Centro, Caracas",
      description: "¡Pozo de premios de $1000! El evento más grande del año.",
      current_participants: 32,
      max_participants: 64,
      format: "Avanzado TCG",
      image: "https://customer-assets.emergentagent.com/job_card-nexus-5/artifacts/32zu9ujh_torneo%203v3%20-%202.jpg",
    },
    {
      id: 2,
      name: "Copa Principiantes",
      date: "2025-12-22",
      location: "Centro Comunitario",
      description: "Perfecto para nuevos duelistas. Aprende y compite.",
      current_participants: 18,
      max_participants: 32,
      format: "Principiante",
      image: "https://customer-assets.emergentagent.com/job_card-nexus-5/artifacts/wajgvn3q_Imagen%20de%20WhatsApp%202025-11-27%20a%20las%2022.15.40_05dc94f0.jpg",
    },
  ];

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    axios.get(`${API}/torneos?limit=6`).then(r => {
      if (r.data.torneos?.length) setTournaments(r.data.torneos);
    }).catch(() => {});
    return () => obs.disconnect();
  }, []);

  const displayTournaments = tournaments.length > 0 ? tournaments.slice(0, 3) : STATIC;

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050508 0%, #0a0512 50%, #050508 100%)' }}
      data-testid="tournaments-section"
    >
      {/* BG glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.3), transparent)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(220,38,38,0.3), transparent)' }} />
      </div>

      <div className="container mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col items-center mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-px" style={{ background: 'linear-gradient(to right, transparent, #dc2626)' }} />
            <span className="text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full" style={{ color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)' }}>Próximos</span>
            <div className="w-12 h-px" style={{ background: 'linear-gradient(to left, transparent, #dc2626)' }} />
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-center" style={{ fontFamily: 'Cinzel, serif', background: 'linear-gradient(135deg,#fff,#d4af37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Torneos
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayTournaments.map((t, i) => {
            const date = new Date(t.date || t.created_at || Date.now());
            const day = date.getDate();
            const month = date.toLocaleDateString('es-ES', { month: 'short' }).toUpperCase();
            const fillPct = t.max_participants ? Math.round((t.current_participants / t.max_participants) * 100) : 50;

            return (
              <div
                key={t.id || i}
                onClick={() => typeof t.id === "string" && navigate(`/torneos/${t.id}`)}
                data-testid={`home-torneo-${t.id}`}
                className="group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1"
                style={{
                  border: '1px solid rgba(212,175,55,0.15)',
                  background: 'linear-gradient(135deg, rgba(10,5,15,0.95), rgba(20,5,5,0.95))',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s`,
                }}
              >
                {/* Top image */}
                {t.image && (
                  <div className="relative overflow-hidden" style={{ height: '160px' }}>
                    <img
                      src={t.image}
                      alt={t.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      style={{ filter: 'brightness(0.7) saturate(1.3)' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(10,5,15,0.95) 100%)' }} />

                    {/* Date badge */}
                    <div className="absolute top-4 left-4 text-center">
                      <div className="px-3 py-1.5 rounded-xl" style={{ background: 'rgba(5,5,8,0.85)', backdropFilter: 'blur(8px)', border: '1px solid rgba(212,175,55,0.3)' }}>
                        <div className="text-2xl font-black leading-none" style={{ color: '#d4af37', fontFamily: 'Cinzel,serif' }}>{day}</div>
                        <div className="text-[10px] font-bold text-gray-400">{month}</div>
                      </div>
                    </div>

                    {/* Format badge */}
                    {t.format && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase" style={{ background: 'rgba(220,38,38,0.9)', color: '#fff' }}>
                          {t.format}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="p-5">
                  <h3 className="font-black text-white mb-1 text-base leading-tight" style={{ fontFamily: 'Cinzel, serif' }}>{t.name}</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{t.description}</p>

                  <div className="flex flex-col gap-2 mb-4">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <MapPin size={12} className="text-pink-400 flex-shrink-0" />
                      <span className="truncate">{t.location}</span>
                    </div>
                    {t.time && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock size={12} className="text-pink-400 flex-shrink-0" />
                        <span>{t.time}</span>
                      </div>
                    )}
                  </div>

                  {/* Participants bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{t.current_participants} inscritos</span>
                      <span>{t.max_participants || '∞'} máx</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${fillPct}%`,
                          background: fillPct > 75 ? 'linear-gradient(to right,#dc2626,#f97316)' : 'linear-gradient(to right,#7c3aed,#d4af37)',
                        }}
                      />
                    </div>
                  </div>

                  <button className="w-full py-2.5 rounded-xl font-black text-xs uppercase tracking-wide transition-all hover:shadow-lg"
                    style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff', boxShadow: '0 4px 15px rgba(220,38,38,0.3)' }}>
                    Registrarse
                  </button>
                </div>

                {/* Hover glow */}
                <div className="absolute inset-0 pointer-events-none rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ boxShadow: 'inset 0 0 30px rgba(212,175,55,0.1)' }} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TournamentsSection;
